import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Icona "i" cerchiata con la descrizione al passaggio del mouse.
function DescriptionInfo({ text, testId }) {
  if (!text) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center text-slate-400 hover:text-purple-700 transition-colors shrink-0"
          aria-label="Descrizione"
          data-testid={testId}
        >
          <Info size={15} />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs leading-snug whitespace-pre-wrap z-[200]">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * UnifiedRequirementsStage (Iter79) — Fase unificata dei requisiti del servizio.
 *
 * Combina in un'unica schermata ordinata:
 *   • Documenti → checkbox "Sono in grado di fornirlo"
 *   • Servizi concatenati → tasti "Acquista" | "Già in mio possesso"
 *
 * L'ordine deriva da `selected.requirements` (se popolato) oppure viene
 * ricomposto dai due campi legacy `required_document_ids` + `chained_service_ids`.
 *
 * Props:
 *   - selected: service (con requirements/legacy fields)
 *   - documentCatalog: lista globale documenti
 *   - servicesById: mappa `_id -> service` per lookup rapido
 *   - documentChecklist: `{docId: bool}` state
 *   - onDocumentToggle(docId, checked)
 *   - chainedChoices: `{serviceId: "purchase"|"owned"}`
 *   - onChainedChoice(serviceId, choice)
 *   - onContinue(): callback quando l'utente conferma
 *   - onDocsDisclaimerRequired(): callback se ci sono doc non spuntati senza ack
 *   - documentsDisclaimerAcked: bool
 */
export default function UnifiedRequirementsStage({
  selected,
  documentCatalog,
  servicesById,
  documentChecklist,
  onDocumentToggle,
  chainedChoices,
  onChainedChoice,
  onContinue,
  onDocsDisclaimerRequired,
  documentsDisclaimerAcked,
  fmtPrice,
  // Iter209ab — Supplementi accumulati dal wizard prezzo flessibile.
  // Ogni elemento: `{ question_id, label, amount }`. Ognuno diventa una
  // riga nell'Anteprima Costi e concorre al totale.
  flexibleSupplements = [],
  onBack,
}) {
  // Ricava la lista ordinata di requisiti
  const items = useMemo(() => {
    if (Array.isArray(selected?.requirements) && selected.requirements.length > 0) {
      return selected.requirements;
    }
    // Fallback: documenti + servizi (legacy)
    const docItems = (selected?.required_document_ids || []).map(id => ({ type: "document", id }));
    const svcItems = (selected?.chained_service_ids || []).map(id => ({ type: "service", id }));
    return [...docItems, ...svcItems];
  }, [selected]);

  const docsById = useMemo(() => {
    const m = new Map();
    for (const d of documentCatalog || []) m.set(d._id, d);
    return m;
  }, [documentCatalog]);

  // Iter131: filtra solo i documenti PRESENTI nel catalogo e ATTIVI.
  // Se un ID `required_document_ids` non esiste (o è stato disattivato),
  // non lo conteggiamo tra i "missingDocs" così il tasto Continua non
  // resta bloccato per documenti fantasma.
  const docItems = items.filter(r => {
    if (r.type !== "document") return false;
    const doc = docsById.get(r.id);
    return !!doc && doc.is_active !== false;
  });
  const missingDocs = docItems.filter(r => !documentChecklist[r.id]);
  const allDocsChecked = docItems.length === 0 || missingDocs.length === 0;

  const svcItems = items.filter(r => r.type === "service" && servicesById?.get(r.id));

  // Un servizio è "risolto" quando ha una scelta (purchase o owned).
  const undecidedServices = svcItems.filter(r => !chainedChoices?.[r.id]);
  const canContinue = undecidedServices.length === 0;

  if (items.length === 0) {
    return (
      <div className="py-4 text-center text-sm text-muted-foreground" data-testid="stage-requirements-empty">
        Nessun requisito da confermare per questo servizio.
        <div className="mt-4">
          <Button onClick={onContinue} className="ht-gradient border-0" data-testid="requirements-continue-btn">
            Continua →
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 py-2" data-testid="stage-requirements">
      <div className="p-3 rounded-md border-l-4 border-purple-600 bg-purple-50/40">
        <div className="text-[10px] uppercase tracking-widest text-purple-800 font-bold mb-1">
          Requisiti per procedere
        </div>
        <p className="text-xs text-slate-700">
          Spunta i documenti che puoi fornire e decidi come gestire i servizi prerequisiti (acquistandoli in blocco o dichiarando di averli già).
        </p>
      </div>

      <TooltipProvider delayDuration={150}>
      <ul className="space-y-2" data-testid="requirements-list">
        {items.map((r, i) => {
          if (r.type === "document") {
            const d = docsById.get(r.id);
            if (!d) return null;
            if (d.is_active === false) return null;
            const checked = !!documentChecklist[r.id];
            return (
              <li
                key={`d-${r.id}`}
                className={`px-3 py-2.5 rounded-lg border transition-colors ${checked ? "bg-emerald-50/40 border-emerald-200" : "bg-white border-slate-200"}`}
                data-testid={`req-doc-${r.id}`}
              >
                <div className="flex items-center gap-3">
                  {/* Contenuto principale (label + descrizione) */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 shrink-0">
                        📄 Documento
                      </span>
                      <span className="font-medium text-sm text-slate-900">{d.name}</span>
                      <DescriptionInfo text={d.description} testId={`req-doc-info-${r.id}`} />
                    </div>
                  </div>
                {/* Iter158: Azione a destra — tasto "✓ Ce l'ho" uniforme.
                    Sostituisce il vecchio checkbox <label><input> per allineare
                    stile e altezza alle righe servizio. Il wrapper ha
                    `min-w-[204px] justify-end` così questo tasto singolo si
                    incolonna sotto il "✓ Ce l'ho" delle righe servizio (che
                    hanno "🛒 Acquista" + "✓ Ce l'ho" nello stesso wrapper). */}
                <div
                  className="shrink-0 flex items-center gap-1 min-w-[204px] justify-end"
                  data-testid={`req-doc-actions-${r.id}`}
                >
                  <button
                    type="button"
                    onClick={() => onDocumentToggle(r.id, !checked)}
                    className={`text-xs px-3 py-1.5 rounded border font-semibold transition-colors whitespace-nowrap ${checked ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-300 hover:border-emerald-400"}`}
                    aria-pressed={checked}
                    data-testid={`req-doc-cb-${r.id}`}
                  >
                    ✓ Ce l'ho
                  </button>
                </div>
                </div>
              </li>
            );
          }
          // r.type === "service"
          const svc = servicesById.get(r.id);
          if (!svc) return null;
          const choice = chainedChoices?.[r.id];
          const price = svc.professional_price || svc.base_price;
          return (
            <li
              key={`s-${r.id}`}
              className={`px-3 py-2.5 rounded-lg border transition-colors ${choice ? "bg-emerald-50/40 border-emerald-200" : "bg-white border-slate-200"}`}
              data-testid={`req-svc-${r.id}`}
            >
              <div className="flex items-center gap-3">
                {/* Contenuto principale (label + descrizione) */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 shrink-0">
                      🔗 Servizio prerequisito
                    </span>
                    <span className="font-medium text-sm text-slate-900">{svc.name}</span>
                    <DescriptionInfo text={svc.description} testId={`req-svc-info-${r.id}`} />
                    {fmtPrice && (
                      <span className="text-xs font-mono text-slate-600 ml-1">{fmtPrice(price)}</span>
                    )}
                  </div>
                </div>
                {/* Iter158: Azioni a destra — wrapper con `min-w-[204px]
                    justify-end` per allineare il tasto "✓ Ce l'ho" delle
                    righe servizio con quello delle righe documento. */}
                <div
                  className="shrink-0 flex items-center gap-1 min-w-[204px] justify-end"
                  data-testid={`req-svc-actions-${r.id}`}
                >
                  <button
                    type="button"
                    onClick={() => onChainedChoice(r.id, "purchase")}
                    className={`text-xs px-3 py-1.5 rounded border font-semibold transition-colors whitespace-nowrap ${choice === "purchase" ? "bg-purple-600 text-white border-purple-600" : "bg-white border-slate-300 hover:border-purple-400"}`}
                    data-testid={`req-svc-purchase-${r.id}`}
                  >
                    🛒 Acquista
                  </button>
                  <button
                    type="button"
                    onClick={() => onChainedChoice(r.id, "owned")}
                    className={`text-xs px-3 py-1.5 rounded border font-semibold transition-colors whitespace-nowrap ${choice === "owned" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-300 hover:border-emerald-400"}`}
                    data-testid={`req-svc-owned-${r.id}`}
                  >
                    ✓ Ce l'ho
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      </TooltipProvider>

      {documentsDisclaimerAcked && missingDocs.length > 0 && (
        <div className="text-xs text-amber-800 bg-amber-100 border border-amber-300 rounded p-2" data-testid="requirements-disclaimer-ack">
          ✅ Hai accettato di procedere sapendo che il servizio potrà essere completato solo dopo la ricezione di <strong>{missingDocs.length}</strong> {missingDocs.length === 1 ? "documento" : "documenti"}.
        </div>
      )}

      {/* Iter81: anteprima costi totali dinamica basata sui servizi selezionati "Acquista".
          Iter209ab: include ora anche i supplementi accumulati dal wizard prezzo flessibile. */}
      {(() => {
        const purchasedSvcs = svcItems
          .map(r => servicesById.get(r.id))
          .filter(s => s && chainedChoices?.[s._id] === "purchase");
        const chainedTotal = purchasedSvcs.reduce(
          (sum, s) => sum + Number(s.professional_price || s.base_price || 0),
          0,
        );
        const primaryPrice = Number(selected?.professional_price || selected?.base_price || 0);
        const flexList = Array.isArray(flexibleSupplements) ? flexibleSupplements : [];
        const flexTotal = flexList.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
        const grandTotal =
          Math.round((primaryPrice + flexTotal + chainedTotal) * 100) / 100;
        if (!fmtPrice) return null;
        return (
          <div
            className="p-3 rounded-md border-l-4 border-purple-500 bg-white shadow-sm"
            data-testid="requirements-cost-preview"
          >
            <div className="text-[10px] uppercase tracking-widest text-purple-800 font-bold mb-2">
              Anteprima costi
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">{selected?.name || "Servizio principale"}</span>
                <span className="font-mono whitespace-nowrap">{fmtPrice(primaryPrice)}<span className="ml-1 text-[0.7em] font-sans font-normal text-slate-500" data-testid="req-cost-primary-iva">+ IVA</span></span>
              </div>
              {flexList.map((s, i) => (
                <div
                  key={`flex-${s.question_id || i}`}
                  className="flex justify-between text-indigo-700"
                  data-testid={`cost-preview-flex-${s.question_id || i}`}
                >
                  <span>+ {s.label}</span>
                  <span className="font-mono">{fmtPrice(Number(s.amount) || 0)}</span>
                </div>
              ))}
              {purchasedSvcs.map(s => (
                <div key={s._id} className="flex justify-between text-emerald-700" data-testid={`cost-preview-item-${s._id}`}>
                  <span>+ {s.name}</span>
                  <span className="font-mono">{fmtPrice(Number(s.professional_price || s.base_price || 0))}</span>
                </div>
              ))}
              <div className="border-t pt-1 mt-1 flex justify-between font-semibold text-slate-900">
                <span>
                  Totale
                  {purchasedSvcs.length > 0 && (
                    <span className="text-xs text-emerald-700 font-normal ml-1">
                      ({purchasedSvcs.length} {purchasedSvcs.length === 1 ? "servizio" : "servizi"} da acquistare)
                    </span>
                  )}
                </span>
                <span className="font-mono text-purple-900 whitespace-nowrap" data-testid="cost-preview-total">
                  {fmtPrice(grandTotal)}
                <span className="ml-1 text-[0.7em] font-sans font-normal text-slate-500" data-testid="req-cost-total-iva">+ IVA</span></span>
              </div>
            </div>
          </div>
        );
      })()}

      <Button
        className="w-full ht-gradient border-0"
        onClick={() => {
          if (!canContinue) return;
          if (!allDocsChecked && !documentsDisclaimerAcked) {
            onDocsDisclaimerRequired?.();
            return;
          }
          onContinue();
        }}
        disabled={!canContinue}
        data-testid="requirements-continue-btn"
      >
        Continua →
        {undecidedServices.length > 0 && (
          <span className="ml-2 text-[11px] opacity-80">
            (scegli per {undecidedServices.length} {undecidedServices.length === 1 ? "servizio" : "servizi"})
          </span>
        )}
      </Button>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="requirements-back-btn"
        >
          ← Cambia immobile
        </button>
      )}
    </div>
  );
}
